import {
  type ChangeEvent,
  type InputEvent,
  type ReactNode,
  useCallback,
  useMemo,
} from 'react';
import { Input, Space } from 'antd';
import {
  FieldPathByValue,
  type FieldPathValue,
  FieldValues,
  useFormContext,
  useWatch,
} from 'react-hook-form';
import { Select } from '../../../common/components';
import { FormField, useGetIdFromName } from '../../../common/form';
import { encodeStringKey } from '../../../common/utils';
import { AlgorithmSourceFormDataType } from '../sources/schema/types';
import { decodeAlgorithmSourceDimensionOptionValue } from '../sources/utils';
import { getAlgorithmSourceShortLabel } from '../utils';

export interface DbSourceDimensionFormFieldProps<Name extends string> {
  name: Name;
  label: ReactNode;
  algorithmSourcesById: Map<number, AlgorithmSourceFormDataType>;
}

export const DbSourceDimensionFormField = <
  FormData extends FieldValues,
  Name extends FieldPathByValue<FormData, string> &
    `${string}.sourceDimension` = FieldPathByValue<FormData, string> &
    `${string}.sourceDimension`,
  Value extends FieldPathValue<FormData, Name> = FieldPathValue<FormData, Name>,
>(
  props: DbSourceDimensionFormFieldProps<Name>,
) => {
  const { name, label, algorithmSourcesById } = props;

  const options = useMemo(
    () =>
      Array.from(algorithmSourcesById.values()).map((source) => ({
        value: source.id,
        label: getAlgorithmSourceShortLabel(source),
      })),
    [algorithmSourcesById],
  );

  const value = useWatch<FormData, Name>({ name });

  const { columnName, algorithmSourceId } = useMemo(() => {
    const { dimensionCode, algorithmSourceId } = value
      ? decodeAlgorithmSourceDimensionOptionValue(value)
      : {};
    return {
      columnName: dimensionCode ?? '',
      algorithmSourceId: algorithmSourceId
        ? algorithmSourceId
        : options[0].value,
    };
  }, [options, value]);

  const getIdFromName = useGetIdFromName();

  const { setValue } = useFormContext<FormData>();

  const handleChange = useCallback(
    (columnName: string, algorithmSourceId = 0) => {
      setValue(
        name,
        encodeStringKey('dimension', columnName, algorithmSourceId) as Value,
      );
    },
    [name, setValue],
  );

  const id = getIdFromName(name);

  return (
    <FormField name={name} label={label} required>
      <Space.Compact style={{ width: '100%' }} className="nodrag">
        <Select
          id={id.replace('sourceDimension', 'algorithmSourceId')}
          options={options}
          value={algorithmSourceId}
          onChange={(algorithmSourceId: number) => {
            handleChange(columnName, algorithmSourceId);
          }}
          style={{ width: 'max-content' }}
        />
        <Input
          id={getIdFromName(name)}
          value={columnName}
          onChange={(event: ChangeEvent<HTMLInputElement>) => {
            const columnName = event.target.value.replace(/\./g, '');
            handleChange(columnName, algorithmSourceId);
          }}
          onBeforeInput={(event: InputEvent<HTMLInputElement>) => {
            if (event.data === '.') {
              event.preventDefault();
            }
          }}
        />
      </Space.Compact>
    </FormField>
  );
};
